<template>
    <div class="pt-4">
        <div v-if="loading" class="space-y-4">
            <USkeleton class="h-7 w-48" />
            <USkeleton class="h-14" />
            <div class="grid gap-4 xl:grid-cols-2">
                <USkeleton class="h-[28rem]" />
                <USkeleton class="h-[28rem]" />
            </div>
        </div>

        <div v-else-if="loadError" class="mt-6 flex flex-col items-center rounded-lg bg-white px-6 py-16 text-center shadow-sm ring-1 ring-black/5">
            <UIcon name="i-ph-warning-duotone" class="size-8 text-red-400" />
            <h2 class="mt-3 text-base font-semibold text-gray-800">Couldn't load your schedule</h2>
            <p class="mt-1 text-sm text-gray-500">{{ loadError }}</p>
            <UButton @click="retry()" :loading="retrying" color="white" class="mt-5" icon="i-ph-arrow-clockwise-bold">Retry</UButton>
        </div>

        <div v-else-if="!profile" class="mt-6 flex flex-col items-center rounded-lg bg-white px-6 py-16 text-center shadow-sm ring-1 ring-black/5">
            <UIcon name="i-ph-stethoscope-duotone" class="size-8 text-gray-400" />
            <h2 class="mt-3 text-base font-semibold text-gray-800">No doctor profile found</h2>
            <p class="mt-1 text-sm text-gray-500">Your account is not linked to a doctor profile yet. Contact the telemedicine team.</p>
        </div>

        <template v-else>
            <div class="mb-4">
                <h1 class="text-2xl font-semibold text-gray-700">My schedule</h1>
                <p class="mt-1 text-sm text-gray-500">Set the hours patients can book you. Changes apply straight away.</p>
            </div>

            <UAlert
                v-if="profile.status !== DoctorStatus.published"
                color="amber"
                variant="subtle"
                icon="i-ph-eye-slash-duotone"
                title="Patients can't book you yet."
                description="Your profile isn't published. These slots go live in the app once the telemedicine team publishes it."
                class="mb-4"
            />
            <UAlert
                v-else-if="profile.specialty?.active === false"
                color="amber"
                variant="subtle"
                icon="i-ph-eye-slash-duotone"
                title="Patients can't book you right now: your specialty is inactive. Contact the telemedicine team."
                class="mb-4"
            />

            <TelemedicineScheduleEditor
                :doctor-id="profile.id"
                :timezone="profile.timezone"
                :duration-min="profile.session_duration_min"
                :buffer-min="profile.buffer_min"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import { DoctorStatus, type DoctorProfile } from '~/types/telemedicine'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.telemedicineDoctor] })
useHead({ title: 'Telemedicine – My schedule' })

type ScheduleProfile = Pick<DoctorProfile, 'id' | 'timezone' | 'session_duration_min' | 'buffer_min' | 'status'> & {
    specialty: { active: boolean } | null
}

const client = useSupabaseClient()
const auth = useAuthStore()
const toast = useToast()

const loading = ref(true)
const retrying = ref(false)
const loadError = ref<string>()
const profile = ref<ScheduleProfile>()

onMounted(async () => {
    try {
        await load()
    } finally {
        loading.value = false
    }
})

async function load() {
    const { data, error } = await client.from('doctor_profiles')
        .select('id, timezone, session_duration_min, buffer_min, status, specialty:specialties(active)')
        .eq('user_id', auth.user!.id)
        .maybeSingle()
    if (error) {
        loadError.value = error.message
        toast.add({ title: 'Could not load your schedule', description: error.message, icon: 'i-ph-warning-duotone', color: 'red' })
        return
    }
    loadError.value = undefined
    profile.value = (data ?? undefined) as unknown as ScheduleProfile | undefined
}

async function retry() {
    retrying.value = true
    try {
        await load()
    } finally {
        retrying.value = false
    }
}
</script>
